// Lee y guarda los datos de "Editar producto" (ADR-2026-10-21-editar-producto-muestra-sus-datos).
// La lógica (qué cambió, cómo se guarda) es pura y vive en `lib/detallesDelProducto`; acá sólo
// Supabase. El estado lo tiene quien edita y se guarda con "Listo", como el nombre.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { olvidarDelIndice } from "../lib/indiceDeBusquedaDeProductos";
import {
  cambiosDeDetalles,
  detallesDesdeFilas,
  hayCambios,
  sinCategoriasBorradas,
  type DetallesDelProducto,
  type FilaDeProducto,
} from "../lib/detallesDelProducto";

/** `productos_categorias` llega con la migración 20261021200000: sin ella, el campo no se muestra. */
const esTablaAusente = (error: { code?: string } | null): boolean =>
  error?.code === "42P01" || error?.code === "PGRST205";

async function mensajeDeSkuRepetido(sku: string, companyId: string): Promise<string> {
  const { data } = await supabase
    .from("variantes")
    .select("nombre")
    .eq("id_company", companyId)
    .eq("sku", sku)
    .maybeSingle();
  const otro = (data as { nombre?: string | null } | null)?.nombre?.trim();
  return otro
    ? `El SKU ${sku} ya lo tiene "${otro}". Usá otro código.`
    : `El SKU ${sku} ya lo tiene otro producto. Usá otro código.`;
}

export function useDetallesDelProducto(varianteId: string, companyId: string | null) {
  const [original, setOriginal] = useState<DetallesDelProducto | null>(null);
  const [detalles, setDetalles] = useState<DetallesDelProducto | null>(null);
  const [productoId, setProductoId] = useState<string | null>(null);
  /** La marca del producto: de ella son las categorías que se le pueden poner. */
  const [brandId, setBrandId] = useState<string | null>(null);
  const [conCategoriasPropias, setConCategoriasPropias] = useState(false);

  useEffect(() => {
    if (!companyId || !varianteId) return;
    let cancelado = false;
    (async () => {
      const { data: variante, error } = await supabase
        .from("variantes")
        .select("product_id, sku, descripcion, descripcion_visual, atributos")
        .eq("id", varianteId)
        .eq("id_company", companyId)
        .maybeSingle();
      if (cancelado) return;
      if (error) {
        console.error("[useDetallesDelProducto] no se pudo leer la variante", error);
        return;
      }
      const idProducto = (variante as { product_id?: string | null } | null)?.product_id ?? null;
      let producto: (FilaDeProducto & { brand_id?: string | null }) | null = null;
      let categoriaPropia: string | null = null;
      let tablaLista = false;
      if (idProducto) {
        const [{ data }, vinculos] = await Promise.all([
          supabase
            .from("productos")
            .select("categoria_producto, medidas, model_view, brand_id")
            .eq("id", idProducto)
            .eq("id_company", companyId)
            .maybeSingle(),
          supabase
            .from("productos_categorias")
            .select("categoria_id")
            .eq("producto_id", idProducto)
            .eq("id_company", companyId),
        ]);
        if (cancelado) return;
        producto = data;
        tablaLista = !vinculos.error;
        if (vinculos.error && !esTablaAusente(vinculos.error)) {
          console.error("[useDetallesDelProducto] no se pudieron leer las categorías", vinculos.error);
        }
        // Una por producto (índice único desde 20261022120000); si hubiera más, vale la primera.
        categoriaPropia = ((vinculos.data ?? []) as Array<{ categoria_id: string }>)[0]?.categoria_id ?? null;
      }
      const leidos = detallesDesdeFilas(variante, producto, categoriaPropia);
      setBrandId(producto?.brand_id ?? null);
      setConCategoriasPropias(tablaLista);
      setProductoId(idProducto);
      setOriginal(leidos);
      setDetalles(leidos);
    })();
    return () => {
      cancelado = true;
    };
  }, [varianteId, companyId]);

  /**
   * Guarda sólo lo que cambió. Devuelve si la persona corrigió cómo ve la IA el producto:
   * en ese caso quien llama no vuelve a categorizar (la pisaría). Tira si falla un update.
   */
  const guardar = useCallback(async (): Promise<{ descripcionVisualEditada: boolean }> => {
    if (!original || !detalles || !companyId) return { descripcionVisualEditada: false };
    const cambios = cambiosDeDetalles(original, detalles);
    if (!hayCambios(cambios)) return { descripcionVisualEditada: false };
    if (Object.keys(cambios.variante).length > 0) {
      const { error } = await supabase
        .from("variantes")
        .update(cambios.variante)
        .eq("id", varianteId)
        .eq("id_company", companyId);
      // El SKU es único por empresa (`idx_variantes_unique_sku_per_company`): el error de
      // Postgres no le dice nada a la persona, el nombre del otro producto sí.
      if (error?.code === "23505" && cambios.variante.sku) {
        throw new Error(await mensajeDeSkuRepetido(cambios.variante.sku, companyId));
      }
      if (error) throw error;
    }
    if (Object.keys(cambios.producto).length > 0 && productoId) {
      const { error } = await supabase
        .from("productos")
        .update(cambios.producto)
        .eq("id", productoId)
        .eq("id_company", companyId);
      if (error) throw error;
    }
    if (cambios.categoriaPropia && productoId) {
      await guardarCategoriaPropia(productoId, companyId, cambios.categoriaPropia.nueva);
    }
    // Lo que se busca cambió: el buscador lo vuelve a pedir.
    olvidarDelIndice(varianteId);
    setOriginal(detalles);
    return { descripcionVisualEditada: cambios.descripcionVisualEditada };
  }, [original, detalles, companyId, varianteId, productoId]);

  // La descripción de la tienda se muestra sólo si vino con una: escribirla de cero no la lee nadie.
  const conDescripcionDeTienda = !!original?.descripcion.trim();

  /** Después de borrar categorías en el panel: el vínculo ya se fue en cascada, acá también. */
  const podarCategorias = useCallback((existentes: ReadonlySet<string>) => {
    setOriginal((o) => (o ? sinCategoriasBorradas(o, existentes) : o));
    setDetalles((d) => (d ? sinCategoriasBorradas(d, existentes) : d));
  }, []);

  return {
    detalles,
    setDetalles,
    guardar,
    conDescripcionDeTienda,
    brandId,
    conCategoriasPropias,
    podarCategorias,
  };
}

const ERROR_DE_CATEGORIA = "No se pudo guardar la categoría del producto";

/**
 * La categoría del producto (una por producto: índice único por `producto_id`) en UN paso: un upsert.
 * Antes era borrar y crear en dos pedidos, y si el segundo fallaba el producto quedaba sin categoría
 * (ADR-2026-10-23-la-categoria-se-cambia-en-un-paso). Sacarla ya era un solo DELETE.
 *
 * Mientras la política de UPDATE (20261023120100_productos_categorias_cambiar.sql) no esté aplicada, el
 * camino del conflicto lo rechaza RLS (42501): sólo en ese caso se cae al borrar y crear de antes. Un
 * rechazo de otro tipo (la categoría ya no existe) no toca nada.
 */
export async function guardarCategoriaPropia(productoId: string, companyId: string | null, nueva: string | null): Promise<void> {
  const sacar = () => supabase.from("productos_categorias").delete().eq("producto_id", productoId).eq("id_company", companyId);
  if (!nueva) {
    const { error } = await sacar();
    if (error) throw new Error(ERROR_DE_CATEGORIA);
    return;
  }
  const fila = { producto_id: productoId, categoria_id: nueva, id_company: companyId };
  const { error } = await supabase.from("productos_categorias").upsert(fila, { onConflict: "producto_id" });
  if (!error) return;
  if ((error as { code?: string }).code !== "42501") throw new Error(ERROR_DE_CATEGORIA);
  const { error: eSacar } = await sacar();
  if (eSacar) throw new Error(ERROR_DE_CATEGORIA);
  const { error: ePoner } = await supabase.from("productos_categorias").insert(fila);
  if (ePoner) throw new Error(ERROR_DE_CATEGORIA);
}
